/**
 * Startup configuration served by the backend at `GET /client/config`.
 *
 * Two blocks matter to this client:
 *
 *   `model_access` — which subscription tiers may use the FULL model catalogue
 *     (Claude, Gemini, OpenAI, Mistral, Qwen) and which are held to the PiPar
 *     family plus the free open-weight models. The server enforces this on
 *     /chat, /completions and at voice-session start; we read it so the picker
 *     offers exactly what the backend will accept instead of letting a user
 *     select a model that fails on every message. Source: backend
 *     `model_access.yaml` + `services/model_access.py`.
 *
 *   `free_models` — which model ids cost no credits for a given tier, and the
 *     allowance (5h window / weekly cap) that bounds them. Source: backend
 *     `zero_rated_models.yaml` + `services/zero_rating.py`.
 *
 * The endpoint is unauthenticated and therefore not personalised: each entry
 * carries the tiers it applies to, and the caller passes the tier it already
 * knows.
 */

import { API_BASE } from './backendConfig'

export interface ModelAccessConfig {
  /** Tiers that get every model on their SUBSCRIPTION alone. */
  full_catalogue_for: string[]
  /** What a restricted tier with no credits is steered to. The server clamps
   *  voice sessions to this and names it in the `model_access` refusal. */
  fallback_model: string
  /** Smallest credit balance that buys the full catalogue on any tier.
   *  Absent on a backend that predates the rule — treated as unreachable, so
   *  the picker stays subscription-only, which is stricter than that server
   *  but never looser. */
  min_credits_for_catalogue?: number
}

export interface FreeModelEntry {
  id: string
  free_for: string[]
}

export interface FreeModelGuards {
  window_hours?: number
  window_messages?: number
  weekly_days?: number
  weekly_messages?: number
  max_context_tokens?: number
}

export interface FreeModelsConfig {
  models: FreeModelEntry[]
  /** Default allowance. */
  guards: FreeModelGuards
  /** Per-tier overrides; the free and Starter tiers run smaller numbers. */
  tier_guards: Record<string, FreeModelGuards>
}

export interface ClientConfig {
  /** null means the server says there is no catalogue gate — an older backend
   *  or `enabled: false` in model_access.yaml. Distinct from `failed`. */
  model_access: ModelAccessConfig | null
  /** null means zero-rating is off; nothing is free. */
  free_models: FreeModelsConfig | null
  /** True when the fetch itself did not succeed, so neither field above
   *  carries information. Kept separate because "the server told us there is
   *  no gate" and "we could not reach the server" must not read the same. */
  failed: boolean
}

/**
 * Used while the config is still loading and whenever the fetch fails.
 * Mirrors `DEFAULT_CONFIG` in services/model_access.py.
 *
 * Restrictive on purpose: production runs the gate, so guessing "full
 * catalogue for everyone" would show a free user a picker the server refuses
 * on every message. A list that widens once the real config lands is a much
 * better failure than one that narrows.
 */
const DEFAULT_MODEL_ACCESS: ModelAccessConfig = {
  full_catalogue_for: ['trial', 'premium', 'owner'],
  fallback_model: 'pipar-basic',
  min_credits_for_catalogue: 1,
}

let cached: ClientConfig | null = null
let inFlight: Promise<ClientConfig> | null = null

/** Fetch once per page load and memoise. Startup config, not live state. */
export async function loadClientConfig(): Promise<ClientConfig> {
  if (cached) return cached
  if (inFlight) return inFlight

  inFlight = (async () => {
    try {
      const res = await fetch(`${API_BASE}/client/config?platform=web`)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      cached = {
        model_access: (data?.model_access as ModelAccessConfig) ?? null,
        free_models: (data?.free_models as FreeModelsConfig) ?? null,
        failed: false,
      }
    } catch (e) {
      console.warn('[clientConfig] Could not load /client/config:', e)
      // Cache the failure too: retrying on every render turns one failed
      // request into a request per keystroke.
      cached = { model_access: null, free_models: null, failed: true }
    } finally {
      inFlight = null
    }
    return cached
  })()

  return inFlight
}

/** Synchronous read for render paths. Null until loadClientConfig() resolves. */
export function getCachedClientConfig(): ClientConfig | null {
  return cached
}

function normalize(value: string | null | undefined): string {
  return (value || '').trim().toLowerCase()
}

/** The rule in force: the server's, or the restrictive default when we have
 *  none. A successful fetch that carried no `model_access` means the gate is
 *  off server-side, and that answer is authoritative. */
function accessRule(config?: ClientConfig | null): ModelAccessConfig | null {
  const cfg = config ?? cached
  if (!cfg || cfg.failed) return DEFAULT_MODEL_ACCESS
  return cfg.model_access // null = gate disabled server-side
}

/**
 * Does this tier get every model on its subscription alone?
 *
 * An unknown tier — including `null` while the subscription is still loading —
 * reads as restricted, matching `_tier()` in services/model_access.py. Note
 * this is only half the rule: see canUseFullCatalogue.
 */
export function hasFullCatalogue(
  tier: string | null | undefined,
  config?: ClientConfig | null,
): boolean {
  const rule = accessRule(config)
  if (rule === null) return true // no gate
  return rule.full_catalogue_for.map(normalize).includes(normalize(tier))
}

/**
 * May this user actually pick any model right now?
 *
 * CREDITS WIN, matching the server (services/model_access.allows). A balance
 * is money already taken for exactly this, so hiding models a user can pay for
 * would sell them credits and then refuse to spend them — the picker must not
 * be stricter than the gate on that point.
 *
 * `creditsLeft` null means not yet loaded: treated as no credits, so the list
 * grows once the balance arrives rather than shrinking under the user.
 */
export function canUseFullCatalogue(
  tier: string | null | undefined,
  creditsLeft: number | null | undefined,
  config?: ClientConfig | null,
): boolean {
  if (hasFullCatalogue(tier, config)) return true
  const rule = accessRule(config)
  const min = rule?.min_credits_for_catalogue
  if (typeof min !== 'number') return false // backend predates the rule
  return typeof creditsLeft === 'number' && creditsLeft >= min
}

/** What a restricted tier is steered to. */
export function getFallbackModel(config?: ClientConfig | null): string {
  return accessRule(config)?.fallback_model || DEFAULT_MODEL_ACCESS.fallback_model
}

/** Is `modelId` free (zero-rated) for `tier`? False when zero-rating is off. */
export function isFreeModel(
  modelId: string | null | undefined,
  tier: string | null | undefined,
  config?: ClientConfig | null,
): boolean {
  const free = (config ?? cached)?.free_models
  if (!free?.models?.length) return false
  const id = normalize(modelId)
  const t = normalize(tier) || 'free'
  return free.models.some((m) => normalize(m.id) === id && m.free_for.map(normalize).includes(t))
}

/** The allowance that bounds the free models for `tier`. */
export function guardsForTier(
  tier: string | null | undefined,
  config?: ClientConfig | null,
): FreeModelGuards {
  const free = (config ?? cached)?.free_models
  if (!free) return {}
  const t = normalize(tier) || 'free'
  return { ...free.guards, ...(free.tier_guards?.[t] || {}) }
}
